// slotHistory — pure per-pane back/forward history for the shell PageSlot
// (2026-08-06 per Nika "when we navigate accross pageslots - we shuld also
// support back and forward navigations in pageslots").
//
// One history per PANE: the primary (`root._canvasSlot` ↔ `root._slotHistPrimary`)
// and the split second (`root._canvasSlotSecond` ↔ `root._slotHistSecond`) each
// own an independent { stack, index }, so back/forward in one pane can never
// move the other — the same pane-scoping contract the split-view targeting fix
// enforces on every opener.
//
// Pure data → data, zero DOM — the same testability rule
// shared/functions/minimizedSlots.js follows. The REACTIVE side lives in
// canvas/components/PageSlot.js: the pane Slot's lifecycle records every slot
// path change through `histRecord` (a back/forward landing is recognised by
// CURSOR EQUALITY — the arrows write the path the cursor already points at —
// so no "navigating" guard flag exists to drift or desync), an in-place query
// patch through `histReplace` (the page stays, only its route changes), and
// the header arrows drive `histBack`/`histForward`.
//
// Shape: { stack: [href, …], index } — FULL hrefs, query included, matching
// the slot state values verbatim (PageSlot re-mounts on a query change, so a
// deep link is a distinct entry). `index` points at the CURRENT entry.

export const SLOT_HISTORY_CAP = 50

export const histInit = () => ({ stack: [], index: -1 })

const _valid = (h) =>
  !!(
    h &&
    typeof h === 'object' &&
    Array.isArray(h.stack) &&
    Number.isInteger(h.index) &&
    h.index >= -1 &&
    h.index < h.stack.length
  )

// The entry under the cursor — null on an empty/absent/malformed history.
export const histCurrent = (h) => (_valid(h) && h.index >= 0 ? h.stack[h.index] : null)

// Record a slot path change. Returns the SAME reference (===) when nothing
// must change — the recorder uses that identity to skip the root.update
// entirely (which is also what terminates the record→update→record echo):
//   • a falsy/non-string path (a pane close) is never an entry;
//   • a path equal to the cursor is a back/forward landing or a duplicate
//     same-route write — recording it would fork phantom entries.
// A NEW path drops the forward tail (the branch point discards redo, exactly
// like browser history), appends, and caps at `cap` by evicting oldest-first.
export const histRecord = (h, path, cap = SLOT_HISTORY_CAP) => {
  if (!path || typeof path !== 'string') return h
  const base = _valid(h) ? h : histInit()
  if (histCurrent(base) === path) return h === base ? h : base
  const max = Number.isFinite(cap) && cap > 0 ? cap : SLOT_HISTORY_CAP
  const stack = [...base.stack.slice(0, base.index + 1), path]
  const dropped = Math.max(0, stack.length - max)
  return { stack: stack.slice(dropped), index: stack.length - 1 - dropped }
}

// Replace the entry under the cursor. An IN-PLACE query patch is not a
// navigation: a module page clears a link param it consumed (Catalog's
// `?new=1&name=` once its New product form is open) through the shell's
// `setModuleRouteQuery`, and the pane keeps the page it shows. Recording that
// route would add an entry the pane's Back arrow lands on, and the page would
// re-mount and open the consumed form again (lane RQ-2). So the current entry
// moves, the same move `history.replaceState` makes for the address bar, and
// the forward tail stays, as replaceState keeps it. Returns the SAME reference
// when nothing must change (a falsy/non-string path, or the cursor already
// names it). An empty or malformed history has no entry to replace, so the
// path is recorded instead.
export const histReplace = (h, path) => {
  if (!path || typeof path !== 'string') return h
  if (!_valid(h) || h.index < 0) return histRecord(h, path)
  if (h.stack[h.index] === path) return h
  const stack = h.stack.slice()
  stack[h.index] = path
  return { stack, index: h.index }
}

export const histCanBack = (h) => _valid(h) && h.index > 0

export const histCanForward = (h) => _valid(h) && h.index < h.stack.length - 1

// Step the cursor. Returns { hist, path } — the new history object plus the
// path the pane must navigate to — or null when there is nowhere to go, so a
// caller can never half-step (move the cursor without a route to write).
export const histBack = (h) => {
  if (!histCanBack(h)) return null
  const index = h.index - 1
  return { hist: { stack: h.stack, index }, path: h.stack[index] }
}

export const histForward = (h) => {
  if (!histCanForward(h)) return null
  const index = h.index + 1
  return { hist: { stack: h.stack, index }, path: h.stack[index] }
}
